import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getLicenses, createLicense, deactivateLicense, deleteLicense, getMasterKeyQuotas, createMasterKey } from '../services/api';
import { getAdminInfo } from '../services/auth';
import './LicenseManagement.css';

function LicenseManagement() {
  console.log('LicenseManagement COMPONENT RENDERED');
  const { t } = useTranslation();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    product_code: '',
    license_type: '',
    status: '',
  });

  const [newLicense, setNewLicense] = useState({
    product_code: 'ZEITECE',
    license_type: 'TRIAL',
    expires_at: '',
  });

  const [quotas, setQuotas] = useState([]);
  const [masterKeyInput, setMasterKeyInput] = useState('');
  const [masterKeySubmitting, setMasterKeySubmitting] = useState(false);
  const [masterKeyError, setMasterKeyError] = useState('');

  const adminInfo = getAdminInfo();

  const loadLicenses = useCallback(async () => {
    try {
      setLoading(true);
      const activeFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== '')
      );
      const data = await getLicenses(activeFilters);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/9f295211-a98e-4941-b16d-f3672384af61',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LicenseManagement.jsx:loadLicenses:received',message:'Received licenses from API',data:{licenseCount:data.licenses?.length||0,firstLicenseExpiresAt:data.licenses?.[0]?.expires_at,hypothesisId:'A'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1'})}).catch(()=>{});
      // #endregion
      setLicenses(data.licenses || []);
    } catch (err) {
      setError(err.message || t('admin.license_management.errors.load_licenses_failed'));
      setLicenses([]);
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    console.log('LicenseManagement useEffect triggered, loading licenses');
    loadLicenses();
  }, [loadLicenses]);

  // MasterKey/Quoten beim Mount laden, damit die Tabelle angezeigt wird
  useEffect(() => {
    loadQuotas();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadQuotas = async () => {
    try {
      const data = await getMasterKeyQuotas();
      setQuotas(data.quotas || []);
    } catch {
      setQuotas([]);
    }
  };

  const handleCreateMasterKey = async (e) => {
    e.preventDefault();
    const key = masterKeyInput.trim();
    if (!key) return;
    setMasterKeyError('');
    setMasterKeySubmitting(true);
    try {
      await createMasterKey(key);
      setMasterKeyInput('');
      loadQuotas();
      loadLicenses();
    } catch (err) {
      const msg = err.message || t('admin.license_management.errors.masterkey_invalid_or_backend');
      setMasterKeyError(msg);
    } finally {
      setMasterKeySubmitting(false);
    }
  };

  const handleCreateLicense = async (e) => {
    e.preventDefault();
    try {
      await createLicense(
        adminInfo?.id,
        newLicense.product_code,
        newLicense.license_type,
        newLicense.expires_at || null
      );
      setShowCreateModal(false);
      setNewLicense({ product_code: 'ZEITECE', license_type: 'TRIAL', expires_at: '' });
      loadLicenses();
    } catch (err) {
      setError(err.message || t('admin.license_management.errors.create_license_failed'));
    }
  };

  const handleDeactivate = async (licenseKey) => {
    if (window.confirm(t('alerts.confirm.license_deactivate'))) {
      try {
        await deactivateLicense(licenseKey);
        loadLicenses();
      } catch (err) {
        setError(err.message || t('admin.license_management.errors.deactivate_failed'));
      }
    }
  };

  const handleDelete = async (licenseKey) => {
    if (window.confirm(t('alerts.confirm.license_delete'))) {
      try {
        await deleteLicense(licenseKey);
        loadLicenses();
      } catch (err) {
        setError(err.message || t('admin.license_management.errors.delete_failed'));
      }
    }
  };

  const formatDate = (dateStr) => {
    console.log('formatDate CALLED with:', dateStr, 'Type:', typeof dateStr);
    try {
      if (!dateStr) {
        console.log('formatDate: No date string, returning N/A');
        return 'N/A';
      }
      const date = new Date(dateStr);
      console.log('formatDate: Parsed date:', date, 'Valid:', !isNaN(date.getTime()));
      if (isNaN(date.getTime())) {
        console.log('formatDate: Invalid date, returning raw string:', dateStr);
        return dateStr; // Fallback: Zeige rohen String
      }
      // Explizite Formatierung mit padStart für führende Nullen
      // WICHTIG: Verwende UTC-Methoden, um Zeitzonen-Konvertierung zu vermeiden
      const day = String(date.getUTCDate()).padStart(2, '0');
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const year = date.getUTCFullYear();
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      const formatted = `${day}.${month}.${year}, ${hours}:${minutes}:${seconds}`;
      console.log('formatDate: Formatted result:', formatted);
      return formatted;
    } catch (error) {
      console.error('formatDate ERROR:', error, 'dateStr:', dateStr);
      return dateStr || 'N/A';
    }
  };

  return (
    <div className="license-management">
      <div className="page-header">
        <h2>{t('admin.license_management.title')}</h2>
        <div className="page-header-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            {t('admin.license_management.buttons.new_license')}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <section className="master-key-section" style={{ marginBottom: '24px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>{t('admin.license_management.masterkey.title')}</h3>
        <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#6b7280' }}>
          {t('admin.license_management.masterkey.description')}
        </p>
        <form onSubmit={handleCreateMasterKey} style={{ marginBottom: '16px' }}>
          <textarea
            value={masterKeyInput}
            onChange={(e) => setMasterKeyInput(e.target.value)}
            placeholder={t('admin.license_management.masterkey.placeholder')}
            rows={3}
            style={{ width: '100%', maxWidth: '600px', fontFamily: 'monospace', fontSize: '12px', padding: '8px', marginBottom: '8px' }}
          />
          {masterKeyError && <div className="error-message" style={{ marginBottom: '8px' }}>{masterKeyError}</div>}
          <button type="submit" className="btn btn-primary" disabled={masterKeySubmitting || !masterKeyInput.trim()}>
            {masterKeySubmitting ? t('admin.license_management.masterkey.submitting') : t('admin.license_management.masterkey.submit')}
          </button>
        </form>
        {quotas.length > 0 && (
          <table className="admin-table" style={{ fontSize: '13px' }}>
            <thead>
              <tr>
                <th>{t('admin.license_management.masterkey.table.product')}</th>
                <th>{t('admin.license_management.masterkey.table.current')}</th>
                <th>{t('admin.license_management.masterkey.table.max')}</th>
                <th>{t('admin.license_management.masterkey.table.valid_until')}</th>
              </tr>
            </thead>
            <tbody>
              {quotas.map((q) => (
                <tr key={q.id}>
                  <td>{q.product_code === '*' ? t('admin.common.all') : q.product_code}</td>
                  <td>{q.current_count}</td>
                  <td>{q.max_count}</td>
                  <td>{q.valid_until ? new Date(q.valid_until).toLocaleDateString('de-DE') : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <div className="filters">
        <select
          value={filters.product_code}
          onChange={(e) => setFilters({ ...filters, product_code: e.target.value })}
        >
          <option value="">{t('admin.license_management.filters.all_products')}</option>
          <option value="ZEITECE">ZEITECE</option>
          <option value="ZEITAUF-MOBIL">ZEITAUF-MOBIL</option>
          <option value="ZEITAUF4">ZEITAUF4</option>
          <option value="ZEITART5">ZEITART5</option>
          <option value="OPUSPLANW2">OPUSPLANW2</option>
        </select>
        <select
          value={filters.license_type}
          onChange={(e) => setFilters({ ...filters, license_type: e.target.value })}
        >
          <option value="">{t('admin.license_management.filters.all_types')}</option>
          <option value="PERMANENT">Permanent</option>
          <option value="SUBSCRIPTION">Subscription</option>
          <option value="NFR">NFR</option>
          <option value="TRIAL">Trial</option>
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">{t('admin.license_management.filters.all_status')}</option>
          <option value="true">{t('admin.common.status.active')}</option>
          <option value="false">{t('admin.common.status.inactive')}</option>
        </select>
      </div>

      {loading ? (
        <div className="loading">{t('admin.license_management.loading')}</div>
      ) : (
        <>
        {console.log('LicenseManagement RENDERING TABLE, licenses count:', licenses.length, 'licenses:', licenses)}
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.license_management.table.license_key')}</th>
              <th>{t('admin.license_management.table.product_code')}</th>
              <th>{t('admin.license_management.table.type')}</th>
              <th>{t('admin.license_management.table.user')}</th>
              <th>{t('admin.license_management.table.activated_at')}</th>
              <th>{t('admin.license_management.table.expires_at')}</th>
              <th>{t('admin.license_management.table.status')}</th>
              <th>{t('admin.license_management.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {licenses.map((license) => {
              // #region agent log
              console.log('Rendering license:', license.id, 'expires_at:', license.expires_at);
              // #endregion
              return (
              <tr key={license.id}>
                <td>
                  <Link to={`/admin/licenses/${license.license_key}`}>
                    {license.license_key}
                  </Link>
                </td>
                <td>{license.product_code}</td>
                <td>{license.license_type}</td>
                <td>{license.user_email || '-'}</td>
                <td>{formatDate(license.activated_at)}</td>
                <td>
                  {/* #region agent log */}
                  {(() => {
                    console.log('DEBUG expires_at RAW:', license.expires_at, 'Type:', typeof license.expires_at);
                    const result = formatDate(license.expires_at);
                    console.log('DEBUG expires_at FORMATTED:', result);
                    return result;
                  })()}
                  {/* #endregion */}
                </td>
                <td>
                  <span className={`badge ${license.is_active ? 'badge-success' : 'badge-danger'}`}>
                    {license.is_active ? t('admin.common.status.active') : t('admin.common.status.inactive')}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <Link to={`/admin/licenses/${license.license_key}`} className="btn btn-secondary">
                      {t('admin.common.buttons.details')}
                    </Link>
                    {license.is_active && (
                      <button
                        className="btn btn-warning"
                        onClick={() => handleDeactivate(license.license_key)}
                      >
                        {t('admin.common.buttons.deactivate')}
                      </button>
                    )}
                    <button
                      className="btn btn-danger"
                      onClick={() => handleDelete(license.license_key)}
                    >
                      {t('admin.common.buttons.delete')}
                    </button>
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
        </>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admin.license_management.create_modal.title')}</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreateLicense}>
              <div className="form-group">
                <label>{t('admin.license_management.create_modal.product_code')}</label>
                <select
                  value={newLicense.product_code}
                  onChange={(e) => setNewLicense({ ...newLicense, product_code: e.target.value })}
                  required
                >
                  <option value="ZEITECE">ZEITECE</option>
                  <option value="ZEITAUF-MOBIL">ZEITAUF-MOBIL</option>
                  <option value="ZEITAUF4">ZEITAUF4</option>
                  <option value="ZEITART5">ZEITART5</option>
                  <option value="OPUSPLANW2">OPUSPLANW2</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('admin.license_management.create_modal.license_type')}</label>
                <select
                  value={newLicense.license_type}
                  onChange={(e) => setNewLicense({ ...newLicense, license_type: e.target.value })}
                  required
                >
                  <option value="PERMANENT">Permanent</option>
                  <option value="SUBSCRIPTION">Subscription</option>
                  <option value="NFR">NFR</option>
                  <option value="TRIAL">Trial (30 Tage)</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('admin.license_management.create_modal.expires_at_optional')}</label>
                <input
                  type="datetime-local"
                  value={newLicense.expires_at}
                  onChange={(e) => setNewLicense({ ...newLicense, expires_at: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  {t('admin.common.buttons.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('admin.common.buttons.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LicenseManagement;






